import type { TFunction } from 'i18next'
import type { User } from '@/types'
import type { NavSection } from '@/types'
import {
  AdjustmentsHorizontalIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BeakerIcon,
  BellIcon,
  BoltIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  GlobeAltIcon,
  HomeIcon,
  LightBulbIcon,
  LinkIcon,
  LockClosedIcon,
  MapPinIcon,
  MegaphoneIcon,
  NoSymbolIcon,
  QueueListIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  PresentationChartLineIcon,
  RectangleStackIcon,
  ServerIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  SwatchIcon,
  TagIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  Squares2X2Icon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'

/**
 * Single source of truth for the role-based navigation tree.
 *
 * Why this lives here, not inside Sidebar.tsx: nav used to render in
 * two places — the desktop sidebar (Sidebar.tsx) and a mobile bottom-nav
 * drawer (MobileNavigation.tsx, removed 2026-07 in favor of the
 * hamburger→sidebar drawer on all sub-desktop widths). The two drifted
 * apart silently — the bottom nav pointed hotel users at /hotels and
 * /rooms (admin-only / non-existent), and affiliate users at /materials
 * and /payouts (both retired in #423; /payouts is back since #1596) —
 * producing 403 toasts and dead links
 * on phones. Keeping the tree here (rather than folding it back into
 * Sidebar.tsx) preserves that single source of truth for any future
 * consumer.
 *
 * If you're adding a new route to App.tsx that should appear in the
 * left rail, add the entry here. Sidebar.tsx handles per-section
 * badges (e.g. unread message count) on top of what we return — keep
 * label/href/icon authoritative here.
 */

export interface NavigationSectionsContext {
  user: User | null | undefined
  t: TFunction
  unreadCount?: number
  // Pending affiliation-request count for the user's primary hotel
  // (user.hotels[0]). Renders as a pill on the "Hotel Affiliation
  // Management" item so the count is always discoverable from the
  // rail, even after the floating toast has been dismissed for the
  // session.
  pendingAffiliationsCount?: number
  // #1210 — true when the user's ONLY affiliate access is Dedicated
  // Affiliates (useDedicatedOnlyAffiliate), which removes the Discovery
  // item from the affiliate branch. Sidebar also passes true while the
  // check is unresolved (default-hidden) so the item never flashes in
  // and disappears for dedicated users. Admin and hotel branches are
  // untouched — the hook can only be true for non-admin users without
  // hotel access, so those branches never coexist with a true flag.
  hideDiscovery?: boolean
  // #1331 Part 3 — unread WhatsApp inbox threads (admins only): a pill on the
  // WhatsApp Inbox item and on the collapsed Communication section.
  whatsappUnreadCount?: number
  // Data issues inbox — open issues (admins only): the pill on the ungrouped
  // "Data issues" link under Dashboard (useAdminNavCounts).
  dataIssuesOpenCount?: number
  // #1998 B3 — true when PAYOUTS_ENABLED is off on this environment (or the
  // check is unresolved), which removes the affiliate's Earnings & Payouts
  // entry. The admin console entry stays: an admin can still read and release.
  hidePayouts?: boolean
}

export type UserRoleFlags = {
  isAdmin: boolean
  isHotel: boolean
  isAffiliate: boolean
  isChainUser: boolean
  hasHotelAccess: boolean
  hasAffiliateAccess: boolean
}

/**
 * Compute role flags from a user object — kept identical to the logic
 * Sidebar.tsx and the (since-removed) MobileNavigation.tsx maintained
 * in parallel before (legacy hotel_admin / affiliate_owner / etc. are
 * still in the wild, so the role union is wider than the canonical
 * four values).
 */
export function getUserRoleFlags(user: User | null | undefined): UserRoleFlags {
  const userType = user?.userType
  const roles = user?.roles ?? []

  const isAdmin =
    userType === 'admin' ||
    userType === 'global_admin' ||
    roles.includes('admin') ||
    roles.includes('global_admin')

  const isHotel =
    userType === 'hotel' ||
    userType === 'hotel_admin' ||
    userType === 'hotel_manager' ||
    userType === 'hotel_staff' ||
    roles.includes('hotel') ||
    roles.includes('hotel_admin')

  const isAffiliate =
    userType === 'affiliate' ||
    userType === 'affiliate_owner' ||
    userType === 'affiliate_manager' ||
    userType === 'affiliate_member' ||
    roles.includes('affiliate') ||
    roles.includes('affiliate_owner')

  const activeHotels = (user?.hotels ?? []).filter((h: any) => h.isActive)
  const isChainUser = activeHotels.length > 1
  // Access flags count ACTIVE seats only — a revoked/expired grant must not
  // keep a whole portal nav alive (BR-5).
  const hasHotelAccess = isHotel || activeHotels.length > 0
  const hasAffiliateAccess = isAffiliate || (user?.affiliates ?? []).some((a: any) => a.isActive)

  return { isAdmin, isHotel, isAffiliate, isChainUser, hasHotelAccess, hasAffiliateAccess }
}

/**
 * Where a caller's commissions live. /commissions/history is the AFFILIATE
 * history: its endpoint 401s for a session without an affiliateId, and the API
 * client turns that 401 into a logout. So platform admins go to the admin
 * commissions grid (/affiliate-reservations: net, revshare-missing markers,
 * all affiliates). Hotel users and affiliates have no commissions page since
 * #2071 hid both the #437 hotel commissions page and the affiliate Commission
 * History, so everyone else falls through to /dashboard.
 */
export function commissionsHomeFor(user: User | null | undefined): string {
  const { isAdmin } = getUserRoleFlags(user)
  if (isAdmin) return '/affiliate-reservations'
  return '/dashboard'
}

/**
 * #1410 — admin rail group order and group-level collapse defaults.
 *
 * NAV_GROUP_ORDER is the sequence the admin branch of getNavigationSections
 * emits (and the navigationSections tests pin). DEFAULT_NAV_GROUP_EXPANDED
 * is the initial open/closed state of each group header in Sidebar.tsx for
 * a user with no stored preference: Admin open, Hotels and Affiliates
 * collapsed. A group that is not listed here is treated as open.
 */
export const NAV_GROUP_ORDER = ['admin', 'hotel', 'affiliate'] as const
export type NavGroup = (typeof NAV_GROUP_ORDER)[number]

export const DEFAULT_NAV_GROUP_EXPANDED: Readonly<Record<NavGroup, boolean>> = {
  admin: true,
  hotel: false,
  affiliate: false,
}

export function getNavigationSections({
  user,
  t,
  unreadCount = 0,
  pendingAffiliationsCount = 0,
  hideDiscovery = false,
  whatsappUnreadCount = 0,
  dataIssuesOpenCount = 0,
  hidePayouts = false,
}: NavigationSectionsContext): NavSection[] {
  const sections: NavSection[] = []
  const { isAdmin, isChainUser, hasHotelAccess, hasAffiliateAccess } =
    getUserRoleFlags(user)
  // Commission Rules/Simulator pages (and their backend endpoints) admit only
  // the strict `admin` role — a global_admin bounces off them. Mirror the
  // page's own ProtectedRoute predicate so the nav never shows items the
  // page would reject.
  const hasStrictAdminRole =
    user?.userType === 'admin' || (user?.roles ?? []).includes('admin')

  if (isAdmin) {
    sections.push({
      label: t('dashboard:dashboard'),
      icon: HomeIcon,
      items: [{ label: t('dashboard:overview'), href: '/dashboard', icon: HomeIcon }],
    })

    // Data issues inbox (spec 2026-09-29 §5): ungrouped, right under Dashboard, so the open
    // count stays visible when the Admin group is collapsed. items[0].label === section.label
    // makes Sidebar render it as one flat link with the pill (Sidebar.tsx isSingleItem).
    const dataIssuesLabel = t('dashboard:adminMenu.dataIssues', 'Data issues')
    sections.push({
      label: dataIssuesLabel,
      icon: ExclamationTriangleIcon,
      items: [{
        label: dataIssuesLabel,
        href: '/admin/data-issues',
        icon: ExclamationTriangleIcon,
        badge: dataIssuesOpenCount > 0 ? String(dataIssuesOpenCount) : undefined,
      }],
    })

    // #1410 — admin rail order is Admin → Hotels → Affiliates (Dashboard
    // stays first, ungrouped). The group ORDER here is what the sidebar
    // renders; NAV_GROUP_ORDER / DEFAULT_NAV_GROUP_EXPANDED above pin the
    // sequence and the group-level collapse defaults (Admin open, Hotels
    // and Affiliates collapsed). Membership of each group is unchanged.
    sections.push({
      group: 'admin',
      label: t('common:messages', 'Messages'),
      icon: ChatBubbleLeftRightIcon,
      items: [
        {
          label: t('common:messages', 'Messages'),
          href: '/messaging',
          icon: ChatBubbleLeftRightIcon,
          badge: unreadCount > 0 ? String(unreadCount) : undefined,
        },
      ],
    })

    sections.push({
      group: 'admin',
      label: t('common:content', 'Content'),
      icon: PhotoIcon,
      items: [
        // The UGC pages below are tenant-scoped: /ugc/review-queue and
        // /ugc/brand-guidelines fetch with `hotelId` (permanent empty state
        // for a hotel-less admin, and the API 400s without it), while
        // /ugc/my-submissions needs an affiliate context. Only
        // /ugc/content-library resolves a platform-admin scope, so gate the
        // tenant items on the same presence flags their pages require.
        ...(hasAffiliateAccess
          ? [{ label: t('common:myContent', 'My Content'), href: '/ugc/my-submissions', icon: PhotoIcon }]
          : []),
        ...(hasHotelAccess
          ? [{ label: t('common:contentReview', 'Content Review'), href: '/ugc/review-queue', icon: EyeIcon }]
          : []),
        { label: t('common:contentLibrary', 'Content Library'), href: '/ugc/content-library', icon: RectangleStackIcon },
        ...(hasHotelAccess
          ? [{ label: t('common:brandGuidelines', 'Brand Guidelines'), href: '/ugc/brand-guidelines', icon: SwatchIcon }]
          : []),
      ],
    })

    // ========== ADMIN SECTIONS (#446) ==========
    sections.push({
      group: 'admin',
      label: t('dashboard:adminMenu.peopleAccess', 'People & Access'),
      icon: UsersIcon,
      items: [
        { label: t('common:users'), href: '/admin/users', icon: UsersIcon },
        { label: t('hotels:allHotels'), href: '/hotels', icon: BuildingOfficeIcon },
        { label: t('affiliates:allAffiliates'), href: '/affiliates', icon: UserGroupIcon },
        { label: t('common:impersonation'), href: '/admin/impersonation', icon: UsersIcon },
        { label: t('dashboard:adminMenu.verification', 'Verification'), href: '/admin/verification', icon: ShieldCheckIcon },
        // #1595 — bank-account verification queue. Payouts only go to a
        // verified account, so this sits next to document Verification.
        // #2109: renamed to Affiliates Commercial; the URL stays.
        { label: t('dashboard:adminMenu.bankAccounts', 'Affiliates Commercial'), href: '/admin/bank-accounts', icon: BanknotesIcon },
        // #1764 — the payouts console (queue, lines, generate, platform
        // minimum). Next to Bank Accounts: payouts go to a verified account.
        { label: t('dashboard:adminMenu.payouts', 'Payouts'), href: '/admin/payouts', icon: CurrencyDollarIcon },
      ],
    })
    sections.push({
      group: 'admin',
      label: t('dashboard:adminMenu.operations', 'Operations'),
      icon: AdjustmentsHorizontalIcon,
      items: [
        // #528 S7 — single unified Onboarding entry. The former
        // "Onboarding Queue" + "Onboarding Lookups" items are now tabs inside
        // /admin/onboarding (Queue / Submissions / Form settings).
        { label: t('common:onboardingManagement'), href: '/admin/onboarding', icon: ClipboardDocumentListIcon },
        { label: t('dashboard:adminMenu.syncConflicts', 'Sync Conflicts'), href: '/admin/sync-conflicts', icon: ArrowsRightLeftIcon },
        // #528 S8 — single unified Contracts entry. The former "Contracts
        // Overview" + "Contract Templates" items are now tabs inside
        // /admin/contracts (Pipeline / All contracts / Templates).
        { label: t('common:contracts', 'Contracts'), href: '/admin/contracts', icon: DocumentTextIcon },
        // #1669 (#1420) — every hotel-affiliate affiliation, hidden and ended
        // included. Next to Contracts: the two are the relationship and its terms.
        { label: t('dashboard:adminMenu.allAffiliations', 'All Affiliations'), href: '/admin/affiliations', icon: LinkIcon },
        { label: t('dashboard:adminMenu.allPromotions', 'All Promotions'), href: '/admin/promotions', icon: TagIcon },
        { label: t('common:mediaEnrichment'), href: '/admin/media-enrichment', icon: PhotoIcon },
        // #474 M0a — admin tool to backfill hotel lat/lng. Lives in
        // Operations alongside Media Enrichment since they're both
        // admin-triggered data-enrichment runs over the hotels table.
        { label: t('common:hotelGeocoding', 'Hotel Geocoding'), href: '/admin/tools/hotel-geocoding', icon: MapPinIcon },
      ],
    })
    // #1089 — unified platform-wide reports. These are NOT the per-tenant views
    // under /hotel-* and /affiliate-*: each aggregates across every hotel AND
    // every affiliate at once, which no tenant-side page does. Kept in the
    // 'admin' group so the rail still reads as Admin / Hotels / Affiliates.
    sections.push({
      group: 'admin',
      label: t('dashboard:adminMenu.adminReports', 'Admin Reports'),
      icon: ChartBarIcon,
      items: [
        // '(platform)' suffixes: identical report names live under Hotel
        // Reports and Affiliate Reports further down the rail — the scope
        // marker keeps the three scopes distinct in nav and search.
        { label: t('dashboard:adminMenu.adminReportsSales', 'Performance – Sales (platform)'), href: '/admin/reports/performance-sales', icon: PresentationChartBarIcon },
        { label: t('dashboard:adminMenu.adminReportsVisibility', 'Performance – Visibility (platform)'), href: '/admin/reports/performance-visibility', icon: ChartPieIcon },
        { label: t('dashboard:adminMenu.adminReportsReservations', 'Reservations (platform)'), href: '/admin/reports/reservations', icon: CalendarDaysIcon },
        // #1655 — triage reservations by internal status (bulk mark).
        { label: t('dashboard:adminMenu.adminReportsReservationReview', 'Reservation Review (internal status)'), href: '/admin/reports/reservation-review', icon: ClipboardDocumentCheckIcon },
        { label: t('dashboard:adminMenu.hotelRoiRanking', 'Hotel ROI Ranking'), href: '/admin/reports/hotels/roi-ranking', icon: PresentationChartLineIcon },
        // #2110: the affiliate perspective of the same profitability report.
        { label: t('dashboard:adminMenu.affiliateProfitability', 'Affiliate Profitability'), href: '/admin/reports/affiliates/profitability', icon: PresentationChartLineIcon },
        { label: t('dashboard:adminMenu.cancellationAnalytics', 'Cancellation Analytics'), href: '/admin/reports/cancellation-analytics', icon: ChartPieIcon },
      ],
    })
    sections.push({
      group: 'admin',
      label: t('dashboard:adminMenu.analytics', 'Analytics'),
      icon: ChartBarIcon,
      items: [
        { label: t('dashboard:adminMenu.cohortAnalysis', 'Cohort Analysis'), href: '/admin/reports/affiliate-cohorts', icon: ChartBarIcon },
        { label: t('dashboard:adminMenu.campaignAttribution', 'Campaign Attribution'), href: '/admin/reports/campaign-attribution', icon: GlobeAltIcon },
        { label: t('dashboard:adminMenu.conversionFunnel', 'Conversion Funnel'), href: '/admin/reports/conversion-funnel', icon: ChartBarIcon },
        { label: t('dashboard:adminMenu.revenueForecast', 'Revenue Forecast'), href: '/admin/reports/revenue-forecast', icon: PresentationChartLineIcon },
      ],
    })
    sections.push({
      group: 'admin',
      label: t('dashboard:adminMenu.system', 'System'),
      icon: ServerIcon,
      items: [
        { label: t('dashboard:adminMenu.systemHealth', 'System Health'), href: '/admin/system-health', icon: ServerIcon },
        { label: t('dashboard:adminMenu.pmsDashboard', 'PMS Dashboard'), href: '/admin/system/pms-dashboard', icon: Squares2X2Icon },
        { label: t('dashboard:adminMenu.pmsSyncHealth', 'PMS Sync Health'), href: '/admin/tools/pms-sync-health', icon: ServerIcon },
        // #1549/#1627 — daily tracking-plugin probe of every hotel site, next to PMS health.
        { label: t('dashboard:adminMenu.siteHealth', 'Site Health'), href: '/admin/tools/site-health', icon: GlobeAltIcon },
        { label: t('dashboard:adminMenu.reservationInspector', 'Reservation Inspector (drill-down)'), href: '/admin/tools/reservation-inspector', icon: MagnifyingGlassIcon },
        // #1560 — manual overrides of reservation values when PMS data is unavailable.
        { label: t('dashboard:adminMenu.reservationOverrides', 'Reservation Overrides'), href: '/admin/system/reservation-overrides', icon: PencilSquareIcon },
        { label: t('dashboard:adminMenu.pmsVerification', 'PMS Verification'), href: '/admin/tools/pms-verification', icon: ScaleIcon },
        { label: t('dashboard:adminMenu.commissionParity', 'Commission Parity'), href: '/admin/tools/commission-parity', icon: ScaleIcon },
        // #2236 — commissions still earning inside a live exclusion; the admin applies the zero / PAID clawback.
        { label: t('dashboard:adminMenu.exclusionRetro', 'Commissions inside an exclusion'), href: '/admin/tools/exclusion-retro', icon: NoSymbolIcon },
        // The rule browser/editor and the simulator are gated to the strict
        // `admin` role by CommissionsPage — hide them from global_admin rather
        // than offering links that bounce back to /dashboard.
        ...(hasStrictAdminRole
          ? [
              { label: t('dashboard:adminMenu.commissionRules', 'Commission Rules'), href: '/admin/commissions/rules', icon: CurrencyDollarIcon },
              { label: t('dashboard:adminMenu.commissionSimulator', 'Commission Simulator'), href: '/admin/commissions/simulator', icon: BeakerIcon },
            ]
          : []),
        { label: t('dashboard:adminMenu.auditLog', 'Audit Log'), href: '/admin/audit-log', icon: ClipboardDocumentListIcon },
        { label: t('common:systemSettings'), href: '/admin/settings', icon: CogIcon },
      ],
    })

    sections.push({
      group: 'admin',
      label: t('common:communication', 'Communication'),
      icon: EnvelopeIcon,
      badge: whatsappUnreadCount > 0 ? String(whatsappUnreadCount) : undefined,
      items: [
        // #1331 Part 3 — first: the page the team opens most (replaces the Slack channel).
        {
          label: t('dashboard:adminMenu.whatsappInbox', 'WhatsApp Inbox'),
          href: '/admin/communication-inbox',
          icon: ChatBubbleOvalLeftEllipsisIcon,
          badge: whatsappUnreadCount > 0 ? String(whatsappUnreadCount) : undefined,
        },
        { label: t('dashboard:adminMenu.communicationTemplates', 'Templates'), href: '/admin/communication-templates', icon: EnvelopeIcon },
        // #1695 — campaigns split by kind: event-triggered vs one-time sends.
        { label: t('dashboard:adminMenu.communicationAutomations', 'Automations'), href: '/admin/communication-automations', icon: BoltIcon },
        { label: t('dashboard:adminMenu.communicationBroadcasts', 'Broadcasts'), href: '/admin/communication-broadcasts', icon: MegaphoneIcon },
        // #1052 part E2 — saved audiences.
        { label: t('dashboard:adminMenu.distributionLists', 'Distribution Lists'), href: '/admin/email-distribution-lists', icon: QueueListIcon },
        // #1052 part C — the never-email list.
        { label: t('dashboard:adminMenu.communicationSuppressions', 'Suppressions'), href: '/admin/communication-suppressions', icon: NoSymbolIcon },
        // S6 (messaging revamp) — user-flagged message queue; the route
        // existed without any entry point.
        { label: t('dashboard:adminMenu.messageModeration', 'Message Moderation'), href: '/admin/messaging/moderation', icon: ShieldExclamationIcon },
        { label: t('common:settings', 'Settings'), href: '/admin/email-settings', icon: CogIcon },
      ],
    })
    // #528 S8 — removed the orphan admin "Contracts" group that pointed at the
    // partner-facing /contracts list. Admins reach contracts via the unified
    // /admin/contracts shell in the Operations group above; the partner
    // /contracts route stays for non-admins.
    sections.push({
      group: 'admin',
      label: t('common:notifications', 'Notifications'),
      icon: BellIcon,
      items: [
        { label: t('dashboard:adminMenu.allNotifications', 'All Notifications'), href: '/notifications', icon: BellIcon },
        { label: t('dashboard:adminMenu.messageDelivery', 'Message Delivery'), href: '/admin/message-delivery', icon: PaperAirplaneIcon },
        { label: t('common:preferences', 'Preferences'), href: '/notifications/preferences', icon: CogIcon },
      ],
    })
    // Currency section is hidden from the sidebar for now — routes
    // remain registered, but the rail no longer surfaces them.
    sections.push({
      group: 'admin',
      label: t('dashboard:hotelMenu.settings'),
      icon: CogIcon,
      items: [
        { label: t('dashboard:hotelMenu.profile'), href: '/profile', icon: UserIcon },
        { label: t('dashboard:hotelMenu.preferences'), href: '/settings/preferences', icon: AdjustmentsHorizontalIcon },
        { label: t('dashboard:hotelMenu.security'), href: '/settings/security', icon: LockClosedIcon },
      ],
    })

    // Admin sees BOTH the hotel-side and affiliate-side trees, so the
    // shared "Key Insights" / "Affiliation Management" / "Promotions"
    // labels collide and render as duplicates in the rail. Use the
    // admin-only disambiguated keys (hotelKeyInsights / affiliate…)
    // so the two halves are distinguishable in EN + HE. Non-admin
    // sidebars below keep the simpler single-context label.
    //
    // `group` chunks the admin rail into Admin / Hotels / Affiliates
    // clusters — Sidebar.tsx renders a collapsible group header (#1410)
    // at the first section of each new group so a 20-item list reads as
    // three scannable groups instead of a wall of links.
    sections.push({
      group: 'hotel',
      label: t('dashboard:adminMenu.hotelKeyInsights', 'Hotel Key Insights'),
      icon: LightBulbIcon,
      items: [{ label: t('dashboard:adminMenu.hotelKeyInsights', 'Hotel Key Insights'), href: '/hotel-dashboard', icon: LightBulbIcon }],
    })
    sections.push({
      group: 'hotel',
      label: t('dashboard:hotelMenu.hotelReports'),
      icon: ChartBarIcon,
      items: [
        { label: t('dashboard:hotelMenu.performanceSales'), href: '/hotel-performance', icon: PresentationChartBarIcon },
        { label: t('dashboard:hotelMenu.performanceVisibility'), href: '/hotel-visibility', icon: ChartPieIcon },
        { label: t('dashboard:hotelMenu.reservations'), href: '/hotel-reservations', icon: CalendarDaysIcon },
      ],
    })
    sections.push({
      group: 'hotel',
      label: t('dashboard:adminMenu.hotelAffiliationManagement', 'Hotel Affiliation Management'),
      icon: UserGroupIcon,
      items: [{
        label: t('dashboard:adminMenu.hotelAffiliationManagement', 'Hotel Affiliation Management'),
        // #1028 — ALWAYS the unfiltered page, badge or not. This used to point
        // at /pending whenever the badge was non-zero, which was harmless only
        // because the page ignored the URL entirely; once the route actually
        // seeds the filter (#1028), the main nav item silently became a
        // filtered view and a hotel user could no longer reach their full
        // affiliation list from the menu. Deep-linking to pending belongs to
        // the notification popup, which is about those specific rows — the nav
        // item is the section itself. The badge still shows the count.
        href: '/hotel-affiliations',
        icon: UserGroupIcon,
        badge: pendingAffiliationsCount > 0 ? String(pendingAffiliationsCount) : undefined,
      }],
    })
    sections.push({
      group: 'hotel',
      label: t('dashboard:hotelMenu.hotelDetails'),
      icon: DocumentTextIcon,
      items: [
        { label: t('dashboard:hotelMenu.hotelDetails'), href: '/hotel-details', icon: DocumentTextIcon },
        // #284 — embedded "Work With Us" builder.
        { label: t('dashboard:hotelMenu.workWithUs', 'Work With Us'), href: '/hotel-work-with-us', icon: DocumentTextIcon },
        // #484.3: Hotel Chain details — visible only when the user is a
        // chain user (admin or hotel-side user with multiple accessible
        // hotels). Non-chain users would land on a "no chain access"
        // empty state and don't need the entry in the menu.
        ...(isAdmin || isChainUser
          ? [{ label: t('dashboard:hotelMenu.chainDetails', 'Hotel Chain Details'), href: '/chain-details', icon: DocumentTextIcon }]
          : []),
      ],
    })
    sections.push({
      group: 'hotel',
      label: t('dashboard:hotelMenu.mediaBundles', 'Media Bundles'),
      icon: PhotoIcon,
      items: [{ label: t('dashboard:hotelMenu.mediaBundles', 'Media Bundles'), href: '/hotel-media-bundles', icon: PhotoIcon }],
    })
    sections.push({
      group: 'hotel',
      label: t('dashboard:adminMenu.hotelPromotions', 'Hotel Promotions'),
      icon: TagIcon,
      items: [{ label: t('dashboard:adminMenu.hotelPromotions', 'Hotel Promotions'), href: '/hotel-promotions', icon: TagIcon }],
    })

    sections.push({
      group: 'affiliate',
      label: t('dashboard:adminMenu.affiliateKeyInsights', 'Affiliate Key Insights'),
      icon: LightBulbIcon,
      items: [{ label: t('dashboard:adminMenu.affiliateKeyInsights', 'Affiliate Key Insights'), href: '/affiliate-dashboard', icon: LightBulbIcon }],
    })
    sections.push({
      group: 'affiliate',
      label: t('dashboard:affiliateMenu.affiliateReports'),
      icon: ChartBarIcon,
      items: [
        { label: t('dashboard:affiliateMenu.performanceSales'), href: '/affiliate-performance', icon: PresentationChartBarIcon },
        { label: t('dashboard:affiliateMenu.performanceVisibility'), href: '/affiliate-visibility', icon: ChartPieIcon },
        { label: t('dashboard:affiliateMenu.reservations'), href: '/affiliate-reservations', icon: CalendarDaysIcon },
      ],
    })
    sections.push({
      group: 'affiliate',
      label: t('dashboard:adminMenu.affiliateAffiliationManagement', 'Affiliate Affiliation Management'),
      icon: BuildingOfficeIcon,
      items: [{ label: t('dashboard:adminMenu.affiliateAffiliationManagement', 'Affiliate Affiliation Management'), href: '/affiliate-hotels', icon: BuildingOfficeIcon }],
    })
    sections.push({
      group: 'affiliate',
      label: t('dashboard:affiliateMenu.discovery'),
      icon: SparklesIcon,
      items: [{ label: t('dashboard:affiliateMenu.discovery'), href: '/discovery', icon: SparklesIcon }],
    })
    // #280 PR3 — Marketplace (campaign tenders). Affiliate section.
    sections.push({
      group: 'affiliate',
      label: t('dashboard:affiliateMenu.marketplace', 'Marketplace'),
      icon: RectangleStackIcon,
      items: [{ label: t('dashboard:affiliateMenu.marketplace', 'Marketplace'), href: '/marketplace', icon: RectangleStackIcon }],
    })
    // /promotions is the affiliate feed — its query is gated on
    // `enabled: !!affiliateId` with no admin fallback, so it can never
    // render for an affiliate-less admin (unlike /hotel-promotions and
    // /admin/promotions, which fetch the full option lists for admins).
    if (hasAffiliateAccess) {
      sections.push({
        group: 'affiliate',
        label: t('dashboard:adminMenu.affiliatePromotions', 'Affiliate Promotions'),
        icon: TagIcon,
        items: [{ label: t('dashboard:adminMenu.affiliatePromotions', 'Affiliate Promotions'), href: '/promotions', icon: TagIcon }],
      })
    }
    sections.push({
      group: 'affiliate',
      label: t('dashboard:affiliateMenu.partnerDetails'),
      icon: DocumentTextIcon,
      items: [{ label: t('dashboard:affiliateMenu.partnerDetails'), href: '/affiliate-details', icon: DocumentTextIcon }],
    })
  }

  if (hasHotelAccess && !isAdmin) {
    sections.push({
      label: t('dashboard:hotelMenu.keyInsights'),
      icon: LightBulbIcon,
      items: [{ label: t('dashboard:hotelMenu.keyInsights'), href: '/hotel-dashboard', icon: LightBulbIcon }],
    })
    sections.push({
      label: t('dashboard:hotelMenu.hotelReports'),
      icon: ChartBarIcon,
      // #2273 — Reports start expanded for hotel users (manual collapse is remembered).
      expandKey: 'section:hotelReports',
      items: [
        { label: t('dashboard:hotelMenu.performanceSales'), href: '/hotel-performance', icon: PresentationChartBarIcon },
        { label: t('dashboard:hotelMenu.performanceVisibility'), href: '/hotel-visibility', icon: ChartPieIcon },
        { label: t('dashboard:hotelMenu.reservations'), href: '/hotel-reservations', icon: CalendarDaysIcon },
      ],
    })
    sections.push({
      label: t('dashboard:hotelMenu.affiliationManagement'),
      icon: UserGroupIcon,
      items: [{
        label: t('dashboard:hotelMenu.affiliationManagement'),
        // #1028 — see the sibling admin item above: the nav item is the
        // section, not the pending subset. Only the notification popup
        // deep-links to /pending.
        href: '/hotel-affiliations',
        icon: UserGroupIcon,
        badge: pendingAffiliationsCount > 0 ? String(pendingAffiliationsCount) : undefined,
      }],
    })
    sections.push({
      label: t('dashboard:hotelMenu.discovery', 'Discovery'),
      icon: SparklesIcon,
      items: [{ label: t('dashboard:hotelMenu.discovery', 'Discovery'), href: '/discovery', icon: SparklesIcon }],
    })
    sections.push({
      label: t('dashboard:hotelMenu.hotelDetails'),
      icon: DocumentTextIcon,
      items: [
        { label: t('dashboard:hotelMenu.hotelDetails'), href: '/hotel-details', icon: DocumentTextIcon },
        // #284 — the "Work With Us" builder is admin-only until a subscription
        // gate exists, so it is intentionally absent from the hotel tree (the
        // admin tree above keeps its entry).
        ...(isChainUser
          ? [{ label: t('dashboard:hotelMenu.chainDetails', 'Hotel Chain Details'), href: '/chain-details', icon: DocumentTextIcon }]
          : []),
      ],
    })
    // #652: Media Bundles was only in the admin tree, so hotel users had no
    // menu entry for /hotel-media-bundles. Mirror the admin item here.
    sections.push({
      label: t('dashboard:hotelMenu.mediaBundles', 'Media Bundles'),
      icon: PhotoIcon,
      items: [{ label: t('dashboard:hotelMenu.mediaBundles', 'Media Bundles'), href: '/hotel-media-bundles', icon: PhotoIcon }],
    })
    // #650: verification + promotions hidden before the prod cut (default off).
    // #968 — Contracts removed from the hotel sidebar: hotel users no longer
    // have contract access. '/contracts' is also dropped from the hotel
    // allow-list in RoleBasedRedirect.tsx so direct URLs redirect away. The
    // affiliate-side entry below and the admin /admin/contracts shell are
    // unaffected.
    // #650: messaging hidden before the prod cut (default off).
    // #736 — Content/UGC removed from the hotel sidebar: hotel users no longer
    // have UGC access. Admins keep their own Content section (in the isAdmin
    // block above). The /ugc routes are also dropped from the hotel allow-list
    // in RoleBasedRedirect.tsx so direct URLs redirect away.
    // #2071 — Commissions removed from the hotel sidebar: hotel users no
    // longer see the #437 hotel commissions page. '/hotel-commissions' is also
    // dropped from the hotel allow-list in RoleBasedRedirect.tsx and App.tsx
    // wraps the route in the admin ProtectedRoute, so direct URLs redirect
    // away. Admins keep URL access to it.
    // #2071 — the Notifications menu removed from the hotel sidebar (hotel and
    // chain hotel users; PM decision, it was first hidden from affiliates
    // only). '/notifications' is dropped from both allow-lists in
    // RoleBasedRedirect.tsx, App.tsx makes the route admin-only and the
    // user-service notification endpoints are admin-only. Admins keep theirs.
    // #737 — Profile always; Preferences/Security only when their flags are on.
    // Team lands the /settings/users management page (previously orphaned:
    // /settings itself had no inbound link, so the only path to it was the
    // URL). The group renders whenever Team or a flagged page exists; it
    // auto-reverts to a lone "Profile" link if Team is ever removed.
    sections.push({
      label: t('dashboard:hotelMenu.settings'),
      icon: CogIcon,
      items: [
        { label: t('dashboard:hotelMenu.team', 'Team'), href: '/settings/users', icon: UsersIcon },
        { label: t('dashboard:hotelMenu.profile'), href: '/profile', icon: UserIcon },
      ],
    })
  }

  if (hasAffiliateAccess && !isAdmin) {
    sections.push({
      label: t('dashboard:affiliateMenu.keyInsights'),
      icon: LightBulbIcon,
      items: [{ label: t('dashboard:affiliateMenu.keyInsights'), href: '/affiliate-dashboard', icon: LightBulbIcon }],
    })
    sections.push({
      label: t('dashboard:affiliateMenu.affiliateReports'),
      icon: ChartBarIcon,
      // #2273 — Reports start expanded for affiliate users (manual collapse is remembered).
      expandKey: 'section:affiliateReports',
      items: [
        { label: t('dashboard:affiliateMenu.performanceSales'), href: '/affiliate-performance', icon: PresentationChartBarIcon },
        { label: t('dashboard:affiliateMenu.performanceVisibility'), href: '/affiliate-visibility', icon: ChartPieIcon },
        { label: t('dashboard:affiliateMenu.reservations'), href: '/affiliate-reservations', icon: CalendarDaysIcon },
      ],
    })
    sections.push({
      label: t('dashboard:affiliateMenu.affiliationManagement'),
      icon: BuildingOfficeIcon,
      items: [{ label: t('dashboard:affiliateMenu.affiliationManagement'), href: '/affiliate-hotels', icon: BuildingOfficeIcon }],
    })
    // #1596 (A2) — Earnings & Payouts. Affiliates only: the payout API reads
    // the session's own affiliate, so the admin tree has no entry.
    // #1998 B3: hidden while payouts are switched off on this environment.
    if (!hidePayouts) {
      sections.push({
        label: t('dashboard:affiliateMenu.earningsPayouts', 'Earnings & Payouts'),
        icon: BanknotesIcon,
        items: [{ label: t('dashboard:affiliateMenu.earningsPayouts', 'Earnings & Payouts'), href: '/payouts', icon: BanknotesIcon }],
      })
    }
    // #1210 — hidden for dedicated-only affiliate users (and while that
    // check resolves); see NavigationSectionsContext.hideDiscovery.
    if (!hideDiscovery) {
      sections.push({
        label: t('dashboard:affiliateMenu.discovery'),
        icon: SparklesIcon,
        items: [{ label: t('dashboard:affiliateMenu.discovery'), href: '/discovery', icon: SparklesIcon }],
      })
    }
    // #280 PR3 — Marketplace (campaign tenders) for affiliate users.
    // #650: marketplace + promotions hidden before the prod cut (default off).
    sections.push({
      label: t('dashboard:affiliateMenu.partnerDetails'),
      icon: DocumentTextIcon,
      items: [{ label: t('dashboard:affiliateMenu.partnerDetails'), href: '/affiliate-details', icon: DocumentTextIcon }],
    })
    // #2071 — Commissions (Commission History) removed from the affiliate
    // sidebar: affiliates no longer see that page. '/commissions' is also
    // dropped from both allow-lists in RoleBasedRedirect.tsx, App.tsx wraps
    // the /commissions routes in the admin ProtectedRoute, and
    // GET /commissions/history is admin-only in partner-service.
    // #2071 — Contracts removed from the affiliate sidebar: affiliates no
    // longer see the /contracts list. RoleBasedRedirect.tsx keeps only the
    // single-contract prefix for them (the signing links land on
    // /contracts/:id), App.tsx makes the list route admin-only, and
    // GET /api/v1/contracts refuses affiliate callers.
    // #2071 — the Notifications menu removed from the affiliate sidebar
    // (both /notifications and /notifications/preferences); the hotel branch
    // above dropped it too, so only admins keep it.
    // #650: verification, messaging and UGC content hidden before
    // the prod cut for affiliate users (default off).
    // #737 — Profile always; Preferences/Security only when their flags are on.
    // When only Profile remains, render a direct "Profile" link instead of a
    // one-item "Settings" group (auto-reverts to the grouped "Settings" menu if
    // the flags are re-enabled).
    sections.push({
      label: t('dashboard:affiliateMenu.profile'),
      icon: UserIcon,
      items: [{ label: t('dashboard:affiliateMenu.profile'), href: '/profile', icon: UserIcon }],
    })
  }

  return sections
}
